import { useEffect, useRef, useState } from 'react';
import { Search, X, Save, Trash2, Scissors } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';

interface Fracionamento {
  id: number;
  produto_pai_id: number;
  produto_filho_id: number;
  fator: string;
  prioridade: number;
  /** O vinculo existe — o trilho que diz quais conversoes pai->filho sao legais. */
  ativo: boolean;
  /** A VENDA abre o pacote sozinha quando o filho falta ([DA-364]). Nasce desligada. */
  baixa_automatica: boolean;
  observacao: string | null;
  pai_codigo?: string | null;
  pai_descricao?: string | null;
  pai_estoque?: string | number | null;
  pai_foto_url?: string | null;
  filho_codigo?: string | null;
  filho_descricao?: string | null;
  filho_estoque?: string | number | null;
  filho_unidade?: string | null;
}

interface ProdutoOp {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  estoque_atual?: string | number | null;
  foto_url?: string | null;
  status?: string | null;
}

/**
 * Miniatura do produto na escolha do pai. O buraco vazio (nao um espaco em branco) e proposital:
 * a lista fica com as descricoes alinhadas na mesma coluna com ou sem foto, e "este produto nao
 * tem foto" e informacao — some o preenchimento, nao o lugar. Mesmo desenho de `.produto-thumb`
 * das listas, num tamanho menor pra manter o dropdown denso.
 */
function ThumbPai({ url, tamanho = 34 }: { url?: string | null; tamanho?: number }) {
  const estilo = { width: tamanho, height: tamanho, flex: `0 0 ${tamanho}px` };
  return url
    ? <img src={url} alt="" loading="lazy" className="produto-thumb" style={estilo} />
    : <div className="produto-thumb-vazio" style={estilo} />;
}

export function SecaoFracionamentos({ produtoId }: { produtoId: number }) {
  const [fracs, setFracs] = useState<Fracionamento[]>([]);
  const [carregando, setCarregando] = useState(true);

  async function carregar() {
    setCarregando(true);
    try {
      const r = await api.get<Fracionamento[]>(`/api/produtos/${produtoId}/fracionamentos`);
      setFracs(r);
    } finally { setCarregando(false); }
  }

  useEffect(() => { void carregar(); /* eslint-disable-next-line */ }, [produtoId]);

  const comoFilho = fracs.find((f) => f.produto_filho_id === produtoId) || null;
  const comoPai = fracs.filter((f) => f.produto_pai_id === produtoId);

  return (
    <div className="space-y-3">
      <VinculoPaiSecao
        filhoId={produtoId}
        vinculo={comoFilho}
        onMudou={carregar}
      />
      {!carregando && comoPai.length > 0 && (
        <FilhosSecao
          filhos={comoPai}
          onMudou={carregar}
        />
      )}
    </div>
  );
}

interface VinculoPaiProps {
  filhoId: number;
  vinculo: Fracionamento | null;
  onMudou: () => void;
}

function VinculoPaiSecao({ filhoId, vinculo, onMudou }: VinculoPaiProps) {
  const toast = useToast();
  const dialog = useDialog();
  const [editando, setEditando] = useState(false);
  const [paiEscolhido, setPaiEscolhido] = useState<ProdutoOp | null>(null);
  const [fator, setFator] = useState('');
  const [observacao, setObservacao] = useState('');
  const [baixaAuto, setBaixaAuto] = useState(false);
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ProdutoOp[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [confirmarRemover, setConfirmarRemover] = useState(false);

  // Busca do produto pai no BACKEND (ilike sobre a base inteira), com debounce.
  // Nao filtrar client-side uma lista com teto de 500: a loja tem >1500 produtos e
  // os parafusos de dimensao numerica ("5,5X3") caem fora dos 500 alfabeticos.
  useEffect(() => {
    if (!paiEscolhido && busca.trim().length >= 1) {
      const q = busca.trim();
      const t = setTimeout(() => {
        api.get<ProdutoOp[]>(`/api/produtos?q=${encodeURIComponent(q)}&com_inativos=1`)
          .then((r) => setResultados(r.filter((p) => p.id !== filhoId).slice(0, 15)))
          .catch(() => setResultados([]));
      }, 250);
      return () => clearTimeout(t);
    }
    setResultados([]);
  }, [busca, filhoId, paiEscolhido]);

  function iniciarEdicao() {
    if (vinculo) {
      setPaiEscolhido({
        id: vinculo.produto_pai_id,
        codigo_interno: vinculo.pai_codigo ?? null,
        descricao: vinculo.pai_descricao ?? `(produto ${vinculo.produto_pai_id})`,
        foto_url: vinculo.pai_foto_url ?? null,
      });
    } else {
      setPaiEscolhido(null);
    }
    setFator(vinculo ? String(vinculo.fator).replace('.', ',') : '');
    setObservacao(vinculo?.observacao || '');
    setBaixaAuto(vinculo?.baixa_automatica ?? false);
    setBusca('');
    setEditando(true);
  }

  function cancelar() {
    setEditando(false);
    setPaiEscolhido(null);
    setBusca('');
  }

  async function salvar() {
    if (!paiEscolhido) { toast.erro('selecione o produto pai'); return; }
    const f = Number(String(fator).replace(',', '.'));
    if (!(f > 0)) { toast.erro('fator deve ser maior que zero'); return; }
    setSalvando(true);
    try {
      if (vinculo) {
        // Sempre delete + post (o backend novo nao tem PUT pra fracionamentos).
        await api.delete(`/api/produtos/fracionamentos/${vinculo.id}`);
      }
      await api.post('/api/produtos/fracionamentos', {
        produto_pai_id: paiEscolhido.id,
        produto_filho_id: filhoId,
        fator: f,
        baixa_automatica: baixaAuto,
        observacao: observacao || null,
      });
      toast.sucesso('vinculo salvo');
      setEditando(false);
      onMudou();
    } catch (e: any) {
      toast.erro(e.message || 'erro ao salvar vinculo');
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!vinculo) return;
    try {
      await api.delete(`/api/produtos/fracionamentos/${vinculo.id}`);
      toast.sucesso('vinculo removido');
      setConfirmarRemover(false);
      onMudou();
    } catch (e: any) {
      toast.erro(e.message);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Vinculo como filho (fracionamento)
        </h2>
        {!editando && (
          <button type="button" onClick={iniciarEdicao} className="text-sm text-blue-600 hover:text-blue-800">
            {vinculo ? 'Editar' : 'Vincular a um pai'}
          </button>
        )}
      </div>

      {!editando && !vinculo && (
        <div className="text-xs text-gray-500 italic">
          Este produto nao esta vinculado a nenhum pai. Ex: se este produto e "UN" e vem de
          "PCT 100un", vincule aqui ao pacote informando o fator 100.
        </div>
      )}

      {!editando && vinculo && (
        <div className="flex items-center justify-between gap-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <ThumbPai url={vinculo.pai_foto_url} tamanho={50} />
          <div className="flex-1 text-xs">
            <div>
              <span className="text-gray-500">Pai: </span>
              <span className="text-gray-500 font-mono">{vinculo.pai_codigo || vinculo.produto_pai_id}</span>
              {' — '}<strong>{vinculo.pai_descricao || `(produto ${vinculo.produto_pai_id})`}</strong>
            </div>
            <div className="text-xs text-gray-600 mt-1">
              Fator: <strong>{String(vinculo.fator).replace('.', ',')}</strong>
              {' '}(1 pai gera {String(vinculo.fator).replace('.', ',')} filhos)
              {vinculo.pai_estoque != null && (
                <> {' • '}Estoque do pai: <strong>{Number(vinculo.pai_estoque)}</strong></>
              )}
            </div>
            <div className="text-xs mt-1">
              {vinculo.baixa_automatica ? (
                <span style={{ background: '#fde68a', color: '#000', fontWeight: 700, borderRadius: 3, padding: '1px 6px' }}>
                  A venda baixa do pai automaticamente
                </span>
              ) : (
                <span className="text-gray-500">
                  A venda <strong>nao</strong> baixa do pai — quem abre o pacote e o estoquista, em
                  Estoque &gt; Reposicoes.
                </span>
              )}
            </div>
            {vinculo.observacao && (
              <div className="text-xs text-gray-500 mt-1">Obs: {vinculo.observacao}</div>
            )}
          </div>
          <button type="button" onClick={() => setConfirmarRemover(true)}
            className="text-red-600 hover:bg-red-50 p-2 rounded" title="Remover vinculo">
            <Trash2 size={16} />
          </button>
        </div>
      )}

      {editando && (
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">Produto pai</label>
            {paiEscolhido ? (
              <div className="flex items-center justify-between gap-2 px-2 py-1.5 border border-gray-300 rounded bg-gray-50">
                <div className="flex items-center gap-2 text-xs">
                  <ThumbPai url={paiEscolhido.foto_url} />
                  <div>
                    <span className="text-gray-500 font-mono">{paiEscolhido.codigo_interno}</span>
                    {' — '}{paiEscolhido.descricao}
                  </div>
                </div>
                <button type="button" onClick={() => { setPaiEscolhido(null); setBusca(''); }}
                  className="text-gray-400 hover:text-gray-700">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center border border-gray-300 rounded px-2 bg-white">
                  <Search size={12} className="text-gray-400" />
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar pai por codigo ou descricao..."
                    className="flex-1 px-2 py-1.5 text-xs border-0 focus:ring-0 focus:outline-none bg-transparent"
                  />
                </div>
                {resultados.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 top-full mt-1 border border-gray-300 rounded bg-white shadow-lg max-h-48 overflow-y-auto">
                    {resultados.map((p) => (
                      <div key={p.id} onClick={() => {
                        if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
                        setPaiEscolhido(p);
                      }}
                        className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0">
                        <ThumbPai url={p.foto_url} />
                        <div>
                          {produtoInativo(p) && <SeloInativo />}
                          <span className="text-gray-500 font-mono">{p.codigo_interno}</span>
                          {' — '}{p.descricao}
                          {p.estoque_atual != null && (
                            <span className="text-xs text-gray-400 ml-2">(estoque {Number(p.estoque_atual)})</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Quantidade de Unidades</label>
              <input type="text" value={fator} onChange={(e) => setFator(e.target.value)} placeholder="Ex: 100"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">Observacao (opcional)</label>
              <input type="text" value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex: 1 caixa = 100 un"
                className="w-full px-2 py-1.5 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          {/* O interruptor da [DA-364]. Nasce DESLIGADO: quem abre pacote e o estoquista, pela tela
              Reposicoes. Ligado, a venda volta a descontar do pai sozinha — e ai o estoque do filho
              vira numero derivado (fica read-only na ficha) e o alerta do orcamento passa a somar o
              pai. O rotulo explica o efeito inteiro porque as tres consequencias andam juntas. */}
          <label className="flex items-start gap-2 p-2 bg-gray-50 border border-gray-200 rounded cursor-pointer">
            <input
              type="checkbox"
              checked={baixaAuto}
              onChange={(e) => setBaixaAuto(e.target.checked)}
              className="mt-0.5"
            />
            <span className="text-xs text-gray-700">
              <strong>Baixar do pai automaticamente na venda</strong>
              <span className="block text-gray-500 mt-0.5">
                Desligado (padrao): pai e filho tem estoques independentes e quem abre o pacote e o
                estoquista, pelo botao <strong>Fracionar</strong> em Estoque &gt; Reposicoes.
                Ligado: a venda abre o pacote sozinha, o Est. Atual do filho vira calculado (nao
                editavel) e o orcamento passa a contar o estoque do pai como disponivel.
              </span>
            </span>
          </label>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancelar}
              className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
              Cancelar
            </button>
            <button type="button" onClick={salvar} disabled={salvando}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              <Save size={12} /> {salvando ? 'Salvando...' : 'Salvar vinculo'}
            </button>
          </div>
        </div>
      )}

      {confirmarRemover && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-xl shadow-xl w-full max-w-md" style={{ backgroundColor: 'var(--panel-bg)', position: 'relative' }}>
            <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={() => setConfirmarRemover(false)} aria-label="Fechar">×</button>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold">Remover vinculo?</h3>
            </div>
            <div className="p-5 text-sm text-gray-700">
              Este produto deixara de ser abastecido automaticamente do pai{' '}
              <strong>{vinculo?.pai_descricao || vinculo?.produto_pai_id}</strong>.
              <br />(Nao afeta estoques ja movimentados.)
            </div>
            <div className="flex justify-end gap-2 px-5 pb-4">
              <button type="button" onClick={() => setConfirmarRemover(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button type="button" onClick={remover}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
                Remover
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface FilhosSecaoProps {
  filhos: Fracionamento[];
  onMudou: () => void;
}

function FilhosSecao({ filhos, onMudou }: FilhosSecaoProps) {
  const toast = useToast();
  const [modal, setModal] = useState<Fracionamento | null>(null);
  const [confirmarRemover, setConfirmarRemover] = useState<Fracionamento | null>(null);
  const [removendo, setRemovendo] = useState(false);

  async function remover() {
    if (!confirmarRemover) return;
    setRemovendo(true);
    try {
      await api.delete(`/api/produtos/fracionamentos/${confirmarRemover.id}`);
      toast.sucesso('vinculo removido');
      setConfirmarRemover(null);
      onMudou();
    } catch (e: any) {
      toast.erro(e.message || 'erro ao remover');
    } finally {
      setRemovendo(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
          Filhos cadastrados (apontam para este produto)
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300 text-left text-xs uppercase text-gray-600">
              <th className="py-2 pr-4 font-semibold">Cod</th>
              <th className="py-2 pr-4 font-semibold">Descricao do filho</th>
              <th className="py-2 pr-4 font-semibold text-right">Fator</th>
              <th className="py-2 pr-4 font-semibold text-right">Estoque filho</th>
              <th className="py-2 pr-4 font-semibold">Obs</th>
              <th className="py-2 pr-4 font-semibold text-right">Acoes</th>
            </tr>
          </thead>
          <tbody>
            {filhos.map((v) => (
              <tr key={v.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="py-2 pr-4 text-gray-500 font-mono">{v.filho_codigo || v.produto_filho_id}</td>
                <td className="py-2 pr-4">{v.filho_descricao || `(produto ${v.produto_filho_id})`}</td>
                <td className="py-2 pr-4 text-right font-medium">
                  {String(v.fator).replace('.', ',')} {v.filho_unidade || ''}
                </td>
                <td className="py-2 pr-4 text-right">
                  {v.filho_estoque != null ? Number(v.filho_estoque).toLocaleString('pt-BR') : '—'}
                </td>
                <td className="py-2 pr-4 text-xs text-gray-500">{v.observacao || ''}</td>
                <td className="py-2 pr-4 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setModal(v)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 transition"
                      title="Abrir pacote(s) e alimentar filho"
                    >
                      <Scissors size={13} /> Fracionar manual
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmarRemover(v)}
                      className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                      title="Remover vinculo"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-gray-500 italic">
          O vinculo e gerenciado no cadastro do filho. Ao vender um filho sem estoque, o sistema
          fraciona automaticamente do pai.
        </p>
      </div>

      {modal && (
        <ModalFracionar
          vinculo={modal}
          onFechar={() => setModal(null)}
          onExecutado={() => { setModal(null); onMudou(); }}
        />
      )}

      {confirmarRemover && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="rounded-xl shadow-xl w-full max-w-md" style={{ backgroundColor: 'var(--panel-bg)', position: 'relative' }}>
            <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={() => setConfirmarRemover(null)} aria-label="Fechar">×</button>
            <div className="px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold">Remover vinculo?</h3>
            </div>
            <div className="p-5 text-sm text-gray-700">
              O filho <strong>{confirmarRemover.filho_descricao || `(produto ${confirmarRemover.produto_filho_id})`}</strong> deixara de ser abastecido automaticamente deste pai.
              <br />(Nao afeta estoques ja movimentados.)
            </div>
            <div className="flex justify-end gap-2 px-5 pb-4">
              <button type="button" onClick={() => setConfirmarRemover(null)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                Cancelar
              </button>
              <button type="button" onClick={remover} disabled={removendo}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                {removendo ? 'Removendo...' : 'Remover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ModalFracionarProps {
  vinculo: Fracionamento;
  onFechar: () => void;
  onExecutado: () => void;
}
function ModalFracionar({ vinculo, onFechar, onExecutado }: ModalFracionarProps) {
  const toast = useToast();
  const [qtdPai, setQtdPai] = useState('1');
  const [executando, setExecutando] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setTimeout(() => inputRef.current?.select(), 50); }, []);

  const qtdNum = Number(String(qtdPai).replace(',', '.')) || 0;
  const fator = Number(vinculo.fator);
  const qtdFilho = qtdNum * fator;
  const estoquePai = Number(vinculo.pai_estoque) || 0;
  const estoqueFilho = Number(vinculo.filho_estoque) || 0;
  const paiDepois = estoquePai - qtdNum;
  const filhoDepois = estoqueFilho + qtdFilho;
  const insuficiente = qtdNum > estoquePai;

  async function executar() {
    if (!(qtdNum > 0)) { toast.erro('informe quantidade maior que zero'); return; }
    if (insuficiente) { toast.erro('estoque insuficiente'); return; }
    setExecutando(true);
    try {
      await api.post(`/api/produtos/fracionamentos/${vinculo.id}/executar`, { quantidade_pai: qtdNum });
      toast.sucesso('fracionamento executado');
      onExecutado();
    } catch (e: any) {
      toast.erro(e.message || 'erro ao executar');
    } finally {
      setExecutando(false);
    }
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="rounded-xl shadow-xl w-full max-w-lg" style={{ backgroundColor: 'var(--panel-bg)' }}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold">Fracionar</h3>
          <button type="button" className="btn-fechar-modal" onClick={onFechar} aria-label="Fechar">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700 leading-relaxed">
            <div><span className="text-gray-500">Pai:</span> <strong>{vinculo.pai_descricao || `(produto ${vinculo.produto_pai_id})`}</strong> (estoque {estoquePai})</div>
            <div><span className="text-gray-500">Filho:</span> <strong>{vinculo.filho_descricao || `(produto ${vinculo.produto_filho_id})`}</strong> (estoque {estoqueFilho})</div>
            <div><span className="text-gray-500">Fator:</span> 1 pai → <strong>{fator}</strong> filhos</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade de pai(s) a abrir</label>
            <input
              ref={inputRef}
              type="text"
              value={qtdPai}
              onChange={(e) => setQtdPai(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !insuficiente && qtdNum > 0) executar(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
            <div className="font-semibold text-amber-900 mb-1">Preview</div>
            <div className="grid grid-cols-2 gap-2 text-amber-900">
              <div>Pai: {estoquePai} → <strong>{paiDepois}</strong> ({qtdNum > 0 ? `-${qtdNum}` : '0'})</div>
              <div>Filho: {estoqueFilho} → <strong>{filhoDepois}</strong> ({qtdFilho > 0 ? `+${qtdFilho}` : '0'})</div>
            </div>
            {insuficiente && (
              <div className="mt-2 text-red-700 font-medium">Estoque insuficiente no pai.</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-4">
          <button onClick={onFechar}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            Cancelar
          </button>
          <button onClick={executar} disabled={executando || insuficiente || !(qtdNum > 0)}
            className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:opacity-50">
            {executando ? 'Executando...' : 'Confirmar fracionamento'}
          </button>
        </div>
      </div>
    </div>
  );
}
